import { ChangeDetectionStrategy, Component, computed, input } from "@angular/core";
import { s25Resource } from "../../../util/s25.resource";
import { PermissionService } from "../../../services/permission.service";
import { Item } from "../../../pojo/Item";
import { S25Url } from "../../../util/core/s25.url";
import { TelemetryService } from "../../../services/telemetry.service";

/** Quiet link from a Connect25 layout view to the matching 25Live record, for users with 25Live access. */
@Component({
    standalone: true,
    selector: "s25-ng-se-25live-link",
    template: `
        @if (hasAccess() && href()) {
            <a [href]="href()" target="_blank" rel="noopener noreferrer" (click)="onOpen()">
                {{ label() }}<span class="visually-hidden"> (opens in new tab)</span>
            </a>
        }
    `,
    styles: `
        :host {
            display: block;
            margin-top: 2rem;
        }

        a {
            color: var(--color--text-muted);
            text-decoration: underline;
        }

        a:focus-visible {
            outline: 2px solid currentColor;
            outline-offset: 2px;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Se25LiveLinkComponent {
    readonly itemTypeId = input.required<Item.Ids.Event | Item.Ids.Organization>();
    readonly itemId = input<number | undefined>();

    private readonly accessResource = s25Resource({
        loader: () => PermissionService.has25LiveAccess(),
    });

    readonly hasAccess = computed(() => !!this.accessResource.value());

    readonly label = computed(
        () => `View this ${this.itemTypeId() === Item.Ids.Event ? "event" : "organization"} in 25Live`,
    );

    readonly href = computed(() => {
        const itemId = this.itemId();
        if (!itemId) return undefined;
        return this.itemTypeId() === Item.Ids.Event
            ? S25Url.getEventDetailsUrl(itemId)
            : S25Url.getOrganizationDetailsUrl(itemId);
    });

    onOpen() {
        TelemetryService.sendWithSub("StudentEngagement", "Layouts", "Open25Live");
    }
}
